import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

/**
 * Generic Bulk Update Modal Component
 * Supports select dropdown or date picker inputs
 * 
 * @param {boolean} open - Whether modal is open
 * @param {Function} onClose - Close handler
 * @param {string} title - Modal title
 * @param {string} description - Description text
 * @param {number} selectedCount - Number of selected items
 * @param {string} inputType - 'select' or 'date'
 * @param {any} value - Current value
 * @param {Function} onValueChange - Value change handler
 * @param {Array} options - Options for select (array of {id, name} or strings)
 * @param {Function} onSubmit - Submit handler
 * @param {boolean} loading - Loading state
 * @param {string} submitText - Submit button text
 * @param {string} submitClassName - Submit button class
 * @param {string} testIdPrefix - Test ID prefix for elements
 */
export default function BulkUpdateModal({
  open,
  onClose,
  title,
  description,
  selectedCount,
  inputType = 'select',
  value,
  onValueChange,
  options = [],
  onSubmit,
  loading = false,
  submitText = 'Update',
  submitClassName = 'bg-blue-600 hover:bg-blue-500',
  testIdPrefix = 'bulk'
}) {
  if (!open) return null;

  const handleClose = () => {
    onClose();
  };

  const isDisabled = inputType === 'select' ? !value : !value;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <Card className="bg-slate-900 border-slate-700 w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-white mb-4">
          {title}
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          {description || `Update ${selectedCount} selected items`}
        </p>
        
        {/* Select Input */}
        {inputType === 'select' && (
          <Select value={value || ''} onValueChange={onValueChange}>
            <SelectTrigger 
              className="bg-slate-950 border-slate-700 text-white mb-4" 
              data-testid={`${testIdPrefix}-select`}
            >
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 max-h-[300px]">
              {options.map((option) => {
                const optionValue = typeof option === 'string' ? option : option.id;
                const optionLabel = typeof option === 'string' ? option : option.name;
                return (
                  <SelectItem key={optionValue} value={optionValue} className="text-slate-300">
                    {optionLabel}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
        
        {/* Date Picker Input */}
        {inputType === 'date' && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal bg-slate-950 border-slate-700 text-white hover:bg-slate-900 mb-4"
                data-testid={`${testIdPrefix}-date-trigger`}
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-slate-500" />
                {value ? format(value, 'MM/dd/yyyy') : <span className="text-slate-500">Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700 z-[100]" align="start">
              <Calendar
                mode="single"
                selected={value}
                onSelect={onValueChange}
                initialFocus
                className="bg-slate-900"
              />
            </PopoverContent>
          </Popover>
        )}
        
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-slate-700 text-slate-300"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isDisabled || loading}
            className={submitClassName}
            data-testid={`${testIdPrefix}-submit-btn`}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {submitText} {selectedCount} Items
          </Button>
        </div>
      </Card>
    </div>
  );
}
